import { Injectable } from '@angular/core';
import { ToastContentComponent } from '@components/toast/toast-content.component';
import { NotificationRef, NotificationService } from '@progress/kendo-angular-notification';
import { Observable, Subject } from 'rxjs';

// Subset of the ngx-toastr options the code base uses. Semantics follow ngx-toastr:
// timeOut 0 or disableTimeOut keeps the toast until it is closed, tapToDismiss defaults to true.
export interface ToastOptions {
    timeOut?: number;
    disableTimeOut?: boolean;
    enableHtml?: boolean;
    closeButton?: boolean;
    tapToDismiss?: boolean;
}

export interface ActiveToast {
    toastId: number;
    onHidden: Observable<void>;
}

type ToastStyle = 'success' | 'error' | 'warning' | 'info';

const DEFAULT_TIMEOUT_MS = 5000;

// Toast notifications on Kendo Notification, keeping the ngx-toastr call shape used in ~135 files:
// success/error/warning/info(message, title?, options?) and remove(toastId). ngx-toastr itself was
// archived in 2026 and has no release that supports Angular 22.
@Injectable({ providedIn: 'root' })
export class ToastService {
    private nextId = 1;
    private readonly active = new Map<number, NotificationRef>();

    constructor(private notificationService: NotificationService) {}

    success(message: string, title?: string, options?: ToastOptions): ActiveToast {
        return this.show('success', message, title, options);
    }

    error(message: string, title?: string, options?: ToastOptions): ActiveToast {
        return this.show('error', message, title, options);
    }

    warning(message: string, title?: string, options?: ToastOptions): ActiveToast {
        return this.show('warning', message, title, options);
    }

    info(message: string, title?: string, options?: ToastOptions): ActiveToast {
        return this.show('info', message, title, options);
    }

    remove(toastId: number): void {
        this.active.get(toastId)?.hide();
    }

    private show(style: ToastStyle, message: string, title?: string, options: ToastOptions = {}): ActiveToast {
        const sticky = options.disableTimeOut || options.timeOut === 0;
        const tapToDismiss = options.tapToDismiss !== false;
        // Kendo ignores hideAfter when the notification is closable, so a close button means sticky.
        // A sticky toast that cannot be tapped away must have the button, or nothing could close it.
        const closable = !!options.closeButton || (sticky && !tapToDismiss);

        const ref = this.notificationService.show({
            content: ToastContentComponent,
            type: { style, icon: true },
            position: { horizontal: 'right', vertical: 'top' },
            animation: { type: 'fade', duration: 300 },
            closable,
            hideAfter: sticky || closable ? 0 : options.timeOut ?? DEFAULT_TIMEOUT_MS,
            cssClass: 'pcs-toast'
        });

        const content = ref.content.instance as ToastContentComponent;
        content.title = title || null;
        content.message = message ?? '';
        content.html = !!options.enableHtml;
        content.dismiss = tapToDismiss ? () => ref.hide() : null;
        ref.content.changeDetectorRef.markForCheck();

        const toastId = this.nextId++;
        const hidden = new Subject<void>();
        this.active.set(toastId, ref);
        ref.afterHide?.subscribe(() => {
            this.active.delete(toastId);
            hidden.next();
            hidden.complete();
        });

        return { toastId, onHidden: hidden.asObservable() };
    }
}
